import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type Filters,
  type TableFilter,
  isFilter,
  isNonNullable,
  isObjectTableFilter,
} from '@ipfabric/utils';
import { getRuleFilter } from '../../Table/components/AdvancedFilter/getFromFilters';
import type { RuleFilter } from '../../Table/components/AdvancedFilter/useAdvancedFilter';
import {
  getDefaultOperator,
  operatorReadableMap,
} from '../../Table/components/SimpleFilter/helpers/filterOperators';
import type { ColumnsByKey } from '../../Table/model/selectors/selectColumnsByKey';

const getFilterDefinition = (
  columnsByKey: ColumnsByKey,
  key: string,
  ruleFilter: RuleFilter | null,
): TableFilter | undefined => {
  let filterDefinition = columnsByKey[key]?.filter;

  if (
    isObjectTableFilter(filterDefinition) &&
    key === ruleFilter?.column?.parent
  ) {
    filterDefinition = filterDefinition.children?.find(
      (x) => x.field === ruleFilter?.column?.key,
    )?.filter;
  }
  return filterDefinition;
};
/**
 * Checks whether passed Filters definition can be expressed with simple filters
 *
 * The function does NOT validate the filters. Invalid filters can produce unexpected results.
 */
export const isFiltersSimple =
  (columnsByKey: ColumnsByKey) =>
  (f: Filters): boolean =>
    Object.entries(f).every(([key, filter]) => {
      if (['and', 'or'].includes(key)) {
        // logical operators not supported
        return false;
      }

      if (!isFilter(filter)) {
        return false;
      }

      const ruleFilter = getRuleFilter(
        filter,
        columnsByKey[key],
        // just dummy format, we don't need the value there, just check operator
        {
          dateFormat: '',
          dateTimeFormat: '',
          isoFormat: '',
          timezone: 'UTC',
        },
      );

      const filterDefinition = getFilterDefinition(
        columnsByKey,
        key,
        ruleFilter,
      );
      if (!filterDefinition || !ruleFilter) {
        stumble(
          `Invalid filter field ${key}, cannot determine whether it is simple filter`,
        );
        return false;
      }

      return (
        // operatorReadableMap is a list of operators supported by simple filter
        (isNonNullable(ruleFilter.operator) &&
          ruleFilter.operator in operatorReadableMap) ||
        // all simple filters support filtering by color
        ruleFilter.operator === 'color' ||
        // default operator is expressed by no prefix in simple filter
        ruleFilter.operator ===
          getDefaultOperator(filterDefinition, ruleFilter.value)
      );
    });
